import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';

/**
 * AdminTransactions — /admin/transactions
 * Immutable trade log with filters + CSV export.
 */
export default function AdminTransactions() {
  const [side, setSide] = useState('all');
  const [clan, setClan] = useState('');
  const [debouncedClan, setDebouncedClan] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [skip, setSkip] = useState(0);
  const PAGE = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedClan(clan.trim().replace(/^@/, '').toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [clan]);

  const fetchRows = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = { limit: PAGE, skip };
      if (side !== 'all') params.side = side;
      if (debouncedClan) params.clan = debouncedClan;
      if (dateFrom) params.from = new Date(dateFrom).toISOString();
      if (dateTo) params.to = new Date(dateTo + 'T23:59:59').toISOString();
      const { data } = await api.get('/admin/transactions', { params });
      setRows(data.transactions || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load transactions');
    } finally { setLoading(false); }
  }, [side, debouncedClan, dateFrom, dateTo, skip]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setSkip(0); }, [side, debouncedClan, dateFrom, dateTo]);

  const doExport = async () => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const token = localStorage.getItem('clanster_access_token');
    const params = new URLSearchParams();
    if (side !== 'all') params.set('side', side);
    if (debouncedClan) params.set('clan', debouncedClan);
    if (dateFrom) params.set('from', new Date(dateFrom).toISOString());
    if (dateTo) params.set('to', new Date(dateTo + 'T23:59:59').toISOString());
    const url = `${base}/api/admin/transactions/export/csv?${params.toString()}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) return;
    const blob = await res.blob();
    const dl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dl; a.download = `clanster_transactions_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(dl);
  };

  return (
    <AdminLayout active="transactions">
      <div className="admin-page" data-testid="admin-transactions-page">
        <header className="admin-page-header row">
          <div>
            <h1 className="admin-page-title">Transactions</h1>
            <p className="admin-page-sub">
              {total.toLocaleString()} total · showing {rows.length}
              {debouncedClan ? ` · @${debouncedClan}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={doExport}
            data-testid="admin-tx-export"
          >
            ⬇️ Export CSV
          </button>
        </header>

        <div className="admin-toolbar">
          <div className="admin-chip-row">
            {['all', 'buy', 'sell'].map((s) => (
              <button
                key={s}
                type="button"
                className={`admin-chip${side === s ? ' active' : ''}`}
                onClick={() => setSide(s)}
                data-testid={`tx-side-${s}`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="admin-search"
            style={{ maxWidth: 220 }}
            placeholder="Filter by @clan…"
            value={clan}
            onChange={(e) => setClan(e.target.value)}
            data-testid="tx-filter-clan"
          />
          <input
            type="date"
            className="admin-search"
            style={{ maxWidth: 160 }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="tx-filter-from"
            placeholder="From"
          />
          <input
            type="date"
            className="admin-search"
            style={{ maxWidth: 160 }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="tx-filter-to"
            placeholder="To"
          />
        </div>

        {err && <div className="admin-error">{err}</div>}
        {loading && <div className="admin-loading">Loading…</div>}
        {!loading && !err && (
          rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-tx-empty">No transactions match the filters.</div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table" data-testid="admin-tx-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Side</th>
                      <th>Clan</th>
                      <th>Trader</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Platform fee</th>
                      <th style={{ textAlign: 'right' }}>Creator fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => {
                      const trader = t.side === 'buy' ? t.buyer : t.seller;
                      return (
                        <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                          <td className="admin-mono admin-small">{formatDate(t.created_at)}</td>
                          <td>
                            <span className={`admin-tag ${t.side === 'buy' ? 'tag-ok' : 'tag-warn'}`}>
                              {t.side}
                            </span>
                          </td>
                          <td>
                            <span className="admin-user-handle">@{t.clan_handle}</span>
                          </td>
                          <td className="admin-small">
                            {trader ? (
                              <>
                                <div>{trader.display_name || '—'}</div>
                                <div className="admin-mono admin-muted">{trader.email}</div>
                              </>
                            ) : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>{t.qty}</td>
                          <td style={{ textAlign: 'right' }} className="admin-mono">{t.total.toFixed(2)}</td>
                          <td style={{ textAlign: 'right' }} className="admin-mono admin-small">{t.fee_platform.toFixed(2)}</td>
                          <td style={{ textAlign: 'right' }} className="admin-mono admin-small">{t.fee_creator.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {total > PAGE && (
                <div className="admin-pagination">
                  <button
                    type="button"
                    className="admin-btn-sm"
                    disabled={skip === 0}
                    onClick={() => setSkip(Math.max(0, skip - PAGE))}
                    data-testid="tx-prev"
                  >
                    ← Prev
                  </button>
                  <span className="admin-pagination-info">
                    {skip + 1}–{Math.min(skip + rows.length, total)} of {total.toLocaleString()}
                  </span>
                  <button
                    type="button"
                    className="admin-btn-sm"
                    disabled={skip + rows.length >= total}
                    onClick={() => setSkip(skip + PAGE)}
                    data-testid="tx-next"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )
        )}
      </div>
    </AdminLayout>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
